/**
 * FHIR-specific HTTP API client.
 *
 * Same as ApiClientService but uses FHIR_API_BASE_URL token. This allows Template (and other MFEs) to use API_BASE_URL
 * for their own backend while FHIR services use a separate base URL.
 */

import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import {
  CLIENT_ID_HEADER,
  IMPERSONATION_SESSION_HEADER,
  TIMEZONE_HEADER,
  VENDOR_ID_HEADER,
  skipInjectedHeaders,
} from '../api/interceptors';
import { FHIR_API_BASE_URL } from './api-client.tokens';

export interface FhirRequestOptions {
  params?: HttpParams | { [key: string]: string | string[] };
  headers?: HttpHeaders | { [key: string]: string | string[] };
  context?: HttpContext;
  reportProgress?: boolean;
  responseType?: 'json' | 'text' | 'blob' | 'arraybuffer';
}

@Injectable()
export class FhirApiClientService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl: string;

  constructor() {
    const injectedUrl = inject(FHIR_API_BASE_URL, { optional: true });
    this.baseUrl = injectedUrl || '';

    if (!this.baseUrl) {
      console.warn('[FhirApiClientService] FHIR_API_BASE_URL not provided. Requests may fail.');
    }
  }

  /** GET request */
  get<T>(path: string, options?: FhirRequestOptions): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.get<T>(url, {
      params: options?.params,
      headers: options?.headers,
      context: this.buildRequestContext(options),
      reportProgress: options?.reportProgress,
      responseType: (options?.responseType as 'json') ?? 'json',
    });
  }

  /** POST request */
  post<T>(path: string, body: unknown, options?: FhirRequestOptions): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.post<T>(url, body, {
      params: options?.params,
      headers: options?.headers,
      context: this.buildRequestContext(options),
      reportProgress: options?.reportProgress,
      responseType: (options?.responseType as 'json') ?? 'json',
    });
  }

  /** PUT request */
  put<T>(path: string, body: unknown, options?: FhirRequestOptions): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.put<T>(url, body, {
      params: options?.params,
      headers: options?.headers,
      context: this.buildRequestContext(options),
      reportProgress: options?.reportProgress,
      responseType: (options?.responseType as 'json') ?? 'json',
    });
  }

  /** PATCH request */
  patch<T>(path: string, body: unknown, options?: FhirRequestOptions): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.patch<T>(url, body, {
      params: options?.params,
      headers: options?.headers,
      context: this.buildRequestContext(options),
      reportProgress: options?.reportProgress,
      responseType: (options?.responseType as 'json') ?? 'json',
    });
  }

  /** DELETE request */
  delete<T>(path: string, options?: FhirRequestOptions): Observable<T> {
    const url = this.buildUrl(path);
    return this.http.delete<T>(url, {
      params: options?.params,
      headers: options?.headers,
      context: this.buildRequestContext(options),
      reportProgress: options?.reportProgress,
      responseType: (options?.responseType as 'json') ?? 'json',
    });
  }

  private buildRequestContext(options?: FhirRequestOptions): HttpContext | undefined {
    return skipInjectedHeaders(options?.context, [
      IMPERSONATION_SESSION_HEADER,
      CLIENT_ID_HEADER,
      VENDOR_ID_HEADER,
      TIMEZONE_HEADER,
    ]);
  }

  private buildUrl(path: string): string {
    const base = this.baseUrl.replace(/\/+$/, '');
    const segment = path.startsWith('/') ? path : `/${path}`;
    return `${base}${segment}`;
  }
}
